import { useEffect, useState } from "react";
import AppShell from "@/pages/AppShell";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const PALETTE = ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

const formatIDR = (n) => {
  if (!n) return "Rp 0";
  return "Rp " + Number(n).toLocaleString("id-ID");
};

const Stat = ({ label, value, hint, testid }) => (
  <div className="border border-border bg-card p-5" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-2 font-marketing text-3xl tracking-tight" data-testid={`${testid}-value`}>{value}</div>
    {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
  </div>
);

function downloadCsv(type, days) {
  const TOKEN_KEY = "aivos_token";
  const tok = localStorage.getItem(TOKEN_KEY);
  const url = `${API}/admin/analytics/export.csv?type=${type}&days=${days}`;
  fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {}, credentials: "include" })
    .then((r) => {
      if (!r.ok) throw new Error("Export failed (" + r.status + ")");
      return r.blob();
    })
    .then((blob) => {
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `aivos-${type}-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success(`Downloaded ${type}.csv`);
    })
    .catch((err) => toast.error(err.message || "Export failed"));
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (windowDays) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/analytics/overview?days=${windowDays}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const sendDigestNow = async () => {
    try {
      const r = await api.post("/admin/digest/analytics/run-for-me");
      if (r.data?.ok) {
        toast.success("Digest sent to your inbox");
      } else {
        toast.message("Digest computed (Resend not configured — email skipped)");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send digest");
    }
  };

  const triggerRerun = async () => {
    try {
      const r = await api.post("/admin/audits/rerun-all");
      toast.success(`Queued ${r.data?.queued ?? 0} re-audits (skipped ${r.data?.skipped ?? 0})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to trigger re-audits");
    }
  };

  return (
    <AppShell title="Admin Analytics" subtitle="// platform metrics">
      <div data-testid="admin-analytics-page" className="space-y-8">
        {/* Window selector */}
        <div className="flex items-center gap-2" data-testid="analytics-window-selector">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">window</span>
          {[7, 30, 90, 180].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`window-${d}d`}
              className={`px-3 py-1.5 font-mono text-xs uppercase tracking-widest border transition-colors ${
                days === d ? "bg-obsidian text-bone border-obsidian" : "border-border hover:border-obsidian"
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => load(days)}
            data-testid="analytics-refresh"
            className="ml-auto px-4 py-1.5 font-mono text-xs uppercase tracking-widest border border-border hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            onClick={sendDigestNow}
            data-testid="analytics-send-digest"
            className="px-4 py-1.5 font-mono text-xs uppercase tracking-widest border border-border hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors"
          >
            Send digest now
          </button>
          <button
            onClick={triggerRerun}
            data-testid="analytics-trigger-rerun"
            className="px-4 py-1.5 font-mono text-xs uppercase tracking-widest border border-border hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors"
          >
            Trigger re-audits
          </button>
        </div>

        {loading && !data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-card border border-border animate-pulse" />
            ))}
          </div>
        )}

        {data && (
          <>
            {/* Top-level stats */}
            <section data-testid="stats-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Total Users" value={data.users.total} hint={`+${data.users.new_in_window} new in ${data.window_days}d`} testid="stat-total-users" />
              <Stat label="Google Sign-ups" value={data.users.google_signups} hint="Via Emergent Google Auth" testid="stat-google-users" />
              <Stat label="Total Audits" value={data.audits.total} hint={`${data.audits.complete} complete · ${data.audits.failed} failed`} testid="stat-total-audits" />
              <Stat label="Generated Fixes" value={data.audits.total_fixes} hint="All-time" testid="stat-total-fixes" />
              <Stat label="AI Calls (window)" value={data.ai_usage.calls_in_window} hint={`${data.ai_usage.total_tokens_in_window.toLocaleString()} tokens`} testid="stat-ai-calls" />
              <Stat label="AI Errors (window)" value={data.ai_usage.errors_in_window} hint={data.ai_usage.calls_in_window ? `${Math.round(100 * data.ai_usage.errors_in_window / data.ai_usage.calls_in_window)}% error rate` : "0% error rate"} testid="stat-ai-errors" />
              <Stat label="Avg Audit Time" value={`${((data.audits.avg_processing_ms ?? 0) / 1000).toFixed(1)}s`} hint={`${data.audits.in_window} in window`} testid="stat-avg-audit-time" />
              <Stat label="MRR (IDR)" value={formatIDR(data.billing.mrr_idr)} hint={`${data.billing.payments_paid} paid · ${data.billing.payments_pending} pending`} testid="stat-mrr" />
            </section>

            {/* Charts */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-border bg-card p-5" data-testid="chart-signups">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// signups / day</div>
                <div className="mt-2 h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                    <LineChart data={data.charts.signups}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#22c55e" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-border bg-card p-5" data-testid="chart-audits">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// audits / day</div>
                <div className="mt-2 h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                    <BarChart data={data.charts.audits}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            {/* AI by endpoint + plan distribution */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-border bg-card p-5" data-testid="chart-ai-by-endpoint">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// ai calls by endpoint</div>
                {data.ai_usage.by_endpoint.length === 0 ? (
                  <div className="mt-8 text-sm text-muted-foreground">No AI usage in this window yet.</div>
                ) : (
                  <div className="mt-2 h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                      <BarChart data={data.ai_usage.by_endpoint} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="endpoint" type="category" tick={{ fontSize: 10 }} width={140} />
                        <Tooltip />
                        <Bar dataKey="calls" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="border border-border bg-card p-5" data-testid="chart-plans">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// plan distribution</div>
                <div className="mt-2 h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                    <PieChart>
                      <Pie
                        data={data.billing.plans}
                        dataKey="users"
                        nameKey="plan"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry) => `${entry.plan}: ${entry.users}`}
                      >
                        {data.billing.plans.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            {/* AI by endpoint table */}
            {data.ai_usage.by_endpoint.length > 0 && (
              <section className="border border-border bg-card" data-testid="table-ai-endpoint">
                <div className="px-5 py-3 border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// AI usage details</div>
                <table className="w-full text-sm">
                  <thead className="text-left">
                    <tr className="border-b border-border">
                      <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Endpoint</th>
                      <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Calls</th>
                      <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Total Tokens</th>
                      <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Avg Latency</th>
                      <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ai_usage.by_endpoint.map((e) => (
                      <tr key={e.endpoint} className="border-b border-border last:border-0">
                        <td className="px-5 py-2 font-mono text-xs">{e.endpoint}</td>
                        <td className="px-5 py-2">{e.calls}</td>
                        <td className="px-5 py-2">{e.total_tokens.toLocaleString()}</td>
                        <td className="px-5 py-2">{e.avg_latency_ms}ms</td>
                        <td className="px-5 py-2">{e.errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {/* Top domains + top users */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-border bg-card" data-testid="table-top-domains">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// top audited domains</span>
                  <button
                    onClick={() => downloadCsv("domains", days)}
                    data-testid="export-domains-csv"
                    className="px-3 py-1 font-mono text-[10px] uppercase tracking-widest border border-border hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors"
                  >
                    Export CSV
                  </button>
                </div>
                {data.top_domains.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-muted-foreground">No audits yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left">
                      <tr className="border-b border-border">
                        <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Domain</th>
                        <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Audits</th>
                        <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Avg Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_domains.map((d, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="px-5 py-2 font-mono text-xs truncate max-w-[280px]">{d.website_url}</td>
                          <td className="px-5 py-2">{d.audits}</td>
                          <td className="px-5 py-2">{d.avg_score ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="border border-border bg-card" data-testid="table-top-users">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// most active users</span>
                  <button
                    onClick={() => downloadCsv("users", days)}
                    data-testid="export-users-csv"
                    className="px-3 py-1 font-mono text-[10px] uppercase tracking-widest border border-border hover:bg-obsidian hover:text-bone hover:border-obsidian transition-colors"
                  >
                    Export CSV
                  </button>
                </div>
                {data.top_users.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-muted-foreground">No users yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left">
                      <tr className="border-b border-border">
                        <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                        <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Business</th>
                        <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Audits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_users.map((u) => (
                        <tr key={u.user_id} className="border-b border-border last:border-0">
                          <td className="px-5 py-2 text-xs">{u.email || "—"}</td>
                          <td className="px-5 py-2 text-xs">{u.business_name || "—"}</td>
                          <td className="px-5 py-2">{u.audits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}
